import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	getProhibitionLengthLabel,
	useRegistrRidicuContext,
} from '@gov-nx/module/page';
import { DataTable, InfoBanner } from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';

export interface DriversRegistryProhibitionsViewProps {
	code: PageCode;
}

export const DriversRegistryProhibitionsView = ({
	code,
}: DriversRegistryProhibitionsViewProps) => {
	const { data } = useRegistrRidicuContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);

	return (
		<section className="flex flex-col gap-6">
			<section className="py-2">
				<h2 className="text-headline-s!">
					{ts('zakaz-rizeni-motorovych-vozidel')}
				</h2>
				{data?.hasProhibitions ? (
					data?.prohibitions?.map((prohibition) => {
						const prohibitionLengthLabel = getProhibitionLengthLabel(
							prohibition.length,
							ts,
							ts('data.neurceno')
						);

						return (
							<DataTable
								responsive
								key={prohibition.id}>
								<tbody>
									<tr>
										<td className="sm:w-1/2">{ts('data.skupiny')}</td>
										<td className="sm:w-1/2">
											<strong>{prohibition.group}</strong>
										</td>
									</tr>
									<tr>
										<td>{ts('data.datum-zakazu')}</td>
										<td>
											<strong>
												{prohibition.validFrom
													? toDateReadable(prohibition.validFrom)
													: ts('data.neurceno')}{' '}
												-{' '}
												{prohibition.validTo
													? toDateReadable(prohibition.validTo)
													: ts('data.neurceno')}
											</strong>
										</td>
									</tr>
									<tr>
										<td>{ts('data.delka')}</td>
										<td>
											<strong>{prohibitionLengthLabel}</strong>
										</td>
									</tr>
								</tbody>
							</DataTable>
						);
					})
				) : (
					<InfoBanner
						headline={ts('stav.chyba.zadny-zakaz-rizeni')}
						text={ts('stav.chyba.zadny-zakaz-rizeni-popis')}
					/>
				)}
			</section>

			<section className="py-2">
				<h2 className="text-headline-s!">
					{ts('data.duvody-pozbytych-opravneni')}
				</h2>
				{data?.hasRevocations ? (
					data?.revocations?.map((revocation) => (
						<DataTable
							responsive
							key={revocation.id}>
							<tbody>
								<tr>
									<td className="sm:w-1/2">{ts('data.skupiny')}</td>
									<td className="sm:w-1/2">
										<strong>{revocation.group}</strong>
									</td>
								</tr>
								<tr>
									<td>{ts('data.datum-pozbyti')}</td>
									<td>
										<strong>
											{revocation.validFrom
												? toDateReadable(revocation.validFrom)
												: ts('data.neurceno')}{' '}
											-{' '}
											{revocation.validTo
												? toDateReadable(revocation.validTo)
												: ts('data.neurceno')}
										</strong>
									</td>
								</tr>
								<tr>
									<td>{ts('data.duvod')}</td>
									<td>
										<strong>{revocation.reason}</strong>
									</td>
								</tr>
							</tbody>
						</DataTable>
					))
				) : (
					<InfoBanner
						headline={ts('stav.chyba.zadne-duvody-pozbyti')}
						text={ts('stav.chyba.zadne-duvody-pozbyti-popis')}
					/>
				)}
			</section>
		</section>
	);
};
